import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const MIN_LENGTH = 10

export const ChangePasswordPage = () => {
  const { user, changePassword, mustChangePassword } = useAuth()
  const navigate = useNavigate()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  if (user?.is_builtin) {
    return (
      <div className="max-w-lg card p-6">
        <h1 className="text-xl font-bold text-admin-900 mb-2">Alterar senha</h1>
        <p className="text-sm text-admin-600">
          A conta de proprietário integrada é configurada pela variável de ambiente{' '}
          <code>SUPER_ADMIN_PASSWORD</code> e não pode ser alterada aqui.
        </p>
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (newPassword.length < MIN_LENGTH) {
      setError(`A nova senha deve ter pelo menos ${MIN_LENGTH} caracteres.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setError('As novas senhas não coincidem.')
      return
    }
    if (newPassword === currentPassword) {
      setError('A nova senha deve ser diferente da atual.')
      return
    }
    setSaving(true)
    try {
      await changePassword(currentPassword, newPassword)
      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      if (mustChangePassword) navigate('/', { replace: true })
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Falha ao alterar a senha')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-lg space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-admin-900 flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-primary-600" />
          Alterar senha
        </h1>
        <p className="text-sm text-admin-600 mt-1">{user?.email}</p>
      </div>

      {mustChangePassword && (
        <div className="card p-4 border-l-4 border-l-amber-500 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-admin-800">
            Você entrou com uma senha temporária. Escolha uma nova senha para continuar.
          </p>
        </div>
      )}

      {success && !mustChangePassword && (
        <div className="card p-4 border-l-4 border-l-green-500 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <p className="text-sm text-admin-800">Senha alterada com sucesso.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-4">
        <div>
          <label htmlFor="current-password" className="block text-sm font-medium text-admin-700 mb-1">
            {mustChangePassword ? 'Senha temporária' : 'Senha atual'}
          </label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            className="input w-full"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-admin-700 mb-1">
            Nova senha
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            className="input w-full"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={MIN_LENGTH}
            required
          />
          <p className="text-xs text-admin-500 mt-1">Pelo menos {MIN_LENGTH} caracteres.</p>
        </div>
        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-admin-700 mb-1">
            Confirme a nova senha
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            className="input w-full"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button type="submit" className="btn btn-primary w-full" disabled={saving}>
          {saving ? 'Salvando...' : 'Alterar senha'}
        </button>
      </form>
    </div>
  )
}
